import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FileArchive, FileSignature, Send, Upload } from 'lucide-react';
import { useData } from '../../App';
import { Button, Input, Modal, Select } from '../UI';
import { SignInfo } from '../../types';
import { api } from '../../services/api';
import { formatDay, orgDisplayName, todayISO } from '../../utils/format';
import { downloadFile } from '../billing/Setup';
import { errorText } from '../billing/shared';
import { confirmDialog } from '../Dialogs';

// ЭДО с самозанятыми (backend/edo.py): задание и акт по выплате подписываются в «LiЧате»,
// договор с разделом «Электронный документооборот» — на бумаге.
export const EDO_FROM = '2026-10-16';

const time = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

/** Отметка подписи на выплате и кнопка «На подпись» / «Отправить заново». Организация без ЭДО — бумага: загрузка скана. */
export const SignLine: React.FC<{ txId: string; info: SignInfo; onChanged: () => void; compact?: boolean; edo?: boolean }> = ({ txId, info, onChanged, compact, edo = true }) => {
    const { showNotification } = useData();
    const [busy, setBusy] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    if (!edo) {
        const upload = async (f: File) => {
            setBusy(true);
            try { await api.uploadScan(txId, f); showNotification('Скан загружен — выплата отмечена подписанной', 'success'); onChanged(); }
            catch (e) { showNotification(errorText(e), 'error'); }
            finally { setBusy(false); }
        };
        return (
            <span className={`inline-flex flex-wrap items-center gap-2 ${compact ? '' : 'text-[11px]'}`}>
                {info.signStatus === 'SIGNED'
                    ? <><span className="text-emerald-700 font-semibold">✓ Подписан на бумаге {time(info.signAt)}</span>
                        {info.signFile && <button className="text-blue-600 hover:underline" onClick={() => downloadFile(info.signFile!)}>скан</button>}</>
                    : <span className="text-slate-500">Не подписан (бумага)</span>}
                <button className="text-blue-600 hover:underline inline-flex items-center gap-0.5 disabled:opacity-50" disabled={busy}
                    onClick={() => fileRef.current?.click()}><Upload size={11} /> {busy ? 'загрузка…' : info.signStatus === 'SIGNED' ? 'ещё скан' : 'загрузить подписанный скан'}</button>
                <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload(f); }} />
            </span>
        );
    }
    const send = async () => {
        setBusy(true);
        try { await api.edoSend(txId); showNotification('Задание и акт отправлены на подпись в «LiЧат»', 'success'); onChanged(); }
        catch (e) { showNotification(errorText(e), 'error'); onChanged(); }
        finally { setBusy(false); }
    };
    const s = info.signStatus;
    const btn = (label: string) => (
        <button className="text-blue-600 hover:underline inline-flex items-center gap-0.5 disabled:opacity-50" disabled={busy} onClick={send}>
            <Send size={11} /> {busy ? 'отправка…' : label}</button>
    );
    return (
        <span className={`inline-flex flex-wrap items-center gap-2 ${compact ? '' : 'text-[11px]'}`}>
            {s === 'SIGNED' && <>
                <span className="text-emerald-700 font-semibold">✓ Подписан {time(info.signAt)}</span>
                {info.signFile && <button className="text-blue-600 hover:underline" onClick={() => downloadFile(info.signFile!)}>подписанный PDF</button>}
            </>}
            {s === 'SENT' && <><span className="text-amber-700">На подписи с {time(info.signAt)}</span>{btn('отправить заново')}</>}
            {s === 'REJECTED' && <><span className="text-red-700 break-words">Отказ от подписи: {info.signNote}</span>{btn('отправить заново')}</>}
            {s === 'ERROR' && <><span className="text-red-700 break-words">Не отправлено: {info.signNote}</span>{btn('повторить')}</>}
            {s === 'CHANGED' && <><span className="text-amber-700">Документы изменены после отправки</span>{btn('отправить заново')}</>}
            {!s && <><span className="text-slate-500">Не подписан</span>{btn('на подпись в «LiЧат»')}</>}
        </span>
    );
};

/** Архив подписанных документов: ZIP с папкой на исполнителя и реестром в Excel. */
export const EdoArchiveModal: React.FC<{ counterpartyIds?: string[]; title?: string; onClose: () => void }> = ({ counterpartyIds, title, onClose }) => {
    const { state, showNotification } = useData();
    const [org, setOrg] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [busy, setBusy] = useState(false);
    const go = async () => {
        setBusy(true);
        try {
            const r = await api.edoArchive({ counterpartyIds: counterpartyIds || [], organizationId: org || null, dateFrom: from || null, dateTo: to || null });
            downloadFile(r.fileName);
            showNotification(`В архиве документов: ${r.count}`, 'success');
            onClose();
        } catch (e) { showNotification(errorText(e), 'error'); }
        finally { setBusy(false); }
    };
    return (
        <Modal isOpen onClose={onClose} title={title || 'Подписанные документы — архив'}>
            <div className="space-y-3 text-sm">
                <p className="text-xs text-slate-500">Для банка или налоговой: ZIP — папка на каждого исполнителя с подписанными PDF
                    (задание, акт и лист подписания: кто, когда, IP, контрольная сумма) и реестр в Excel.</p>
                <Select label="ИП" value={org} onChange={e => setOrg(e.target.value)}
                    options={[{ value: '', label: 'Все' }, ...state.organizations.map(o => ({ value: o.id, label: orgDisplayName(o) }))]} />
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Документы с" type="date" value={from} onChange={e => setFrom(e.target.value)} />
                    <Input label="по" type="date" value={to} onChange={e => setTo(e.target.value)} />
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button type="button" variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                    <Button type="button" size="sm" disabled={busy} onClick={go}><FileArchive className="mr-1 h-3.5 w-3.5" /> {busy ? 'Собираю…' : 'Скачать ZIP'}</Button>
                </div>
            </div>
        </Modal>
    );
};

type Pair = Awaited<ReturnType<typeof api.edoNewContractsPreview>>[number];

/** Новые договоры с разделом ЭДО всем самозанятым с договором — для печати и подписи на бумаге. */
export const EdoNewContractsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const { state, showNotification, refreshData } = useData();
    const [rows, setRows] = useState<Pair[] | null>(null);
    const [picked, setPicked] = useState<Record<string, boolean>>({});
    const [date, setDate] = useState(todayISO());
    const [busy, setBusy] = useState(false);
    const key = (r: Pair) => `${r.organizationId}|${r.counterpartyId}`;
    useEffect(() => {
        api.edoNewContractsPreview().then(r => {
            setRows(r);
            setPicked(Object.fromEntries(r.filter(x => !x.hasEdo).map(x => [key(x), true])));
        }).catch(e => showNotification(errorText(e), 'error'));
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps
    const orgName = useMemo(() => Object.fromEntries(state.organizations.map(o => [o.id, orgDisplayName(o)])), [state.organizations]);
    const chosen = (rows || []).filter(r => !r.hasEdo && picked[key(r)]);
    const go = async () => {
        if (!(await confirmDialog(`Создать ${chosen.length} договор(ов) от ${formatDay(date)}? У прежних договоров срок закончится ${formatDay(new Date(new Date(date).getTime() - 86400000).toISOString().slice(0, 10))}.`))) return;
        setBusy(true);
        try {
            const r = await api.edoNewContracts(date, chosen.map(x => ({ organizationId: x.organizationId, counterpartyId: x.counterpartyId })));
            if (r.fileName) downloadFile(r.fileName);
            showNotification(`Договоров создано: ${r.created}${r.skipped ? `, пропущено (уже есть): ${r.skipped}` : ''}. Распечатайте и подпишите.`, 'success');
            if (r.problems?.length) showNotification(`Не созданы — не хватает данных: ${r.problems.join('; ')}`, 'error');
            refreshData();
            onClose();
        } catch (e) { showNotification(errorText(e), 'error'); }
        finally { setBusy(false); }
    };
    return (
        <Modal isOpen onClose={onClose} title="Новые договоры с разделом «Электронный документооборот»">
            <div className="space-y-3 text-sm">
                <p className="text-xs text-slate-500">Каждой паре «ИП — самозанятый» с договором — новый договор от выбранной даты (до 31 декабря).
                    У прежнего договора срок закончится накануне. Договоры скачаются архивом Word: распечатайте и подпишите на бумаге.
                    После этого задания и акты можно отправлять на подпись в «LiЧат».</p>
                <Input label="Дата договоров" type="date" value={date} onChange={e => setDate(e.target.value)} />
                {!rows ? <p className="text-xs text-slate-400">Загрузка…</p> : (
                    <div className="max-h-[50vh] overflow-auto border rounded divide-y">
                        {rows.map(r => (
                            <label key={key(r)} className={`flex items-start gap-2 px-2 py-1.5 text-xs ${r.hasEdo ? 'opacity-60' : ''}`}>
                                <input type="checkbox" className="mt-0.5" disabled={r.hasEdo} checked={!r.hasEdo && !!picked[key(r)]}
                                    onChange={e => setPicked(p => ({ ...p, [key(r)]: e.target.checked }))} />
                                <span className="min-w-0 flex-1">
                                    <span className="font-medium">{r.name}</span> · {orgName[r.organizationId] || ''}
                                    <span className="block text-slate-500">
                                        {r.hasEdo ? `уже есть договор с ЭДО № ${r.lastContract} от ${formatDay(r.lastContractDate)}`
                                            : `сейчас № ${r.lastContract} от ${formatDay(r.lastContractDate)}`}
                                        {r.lastPayment ? ` · последняя выплата ${formatDay(r.lastPayment)}` : ' · выплат не было'}
                                        {!r.linked && ' · нет связи с CRM («LiЧат»)'}
                                    </span>
                                </span>
                            </label>
                        ))}
                    </div>
                )}
                <div className="flex justify-between items-center gap-2 pt-2 border-t">
                    <span className="text-xs text-slate-500">Выбрано: {chosen.length}</span>
                    <div className="flex gap-2">
                        <Button type="button" variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                        <Button type="button" size="sm" disabled={busy || chosen.length === 0} onClick={go}>
                            <FileSignature className="mr-1 h-3.5 w-3.5" /> {busy ? 'Создаю…' : 'Создать и скачать'}</Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
